import { Controller } from "@hotwired/stimulus"

// A month range-calendar with booked days disabled, plus hour/minute/AM-PM time
// pickers (30-min). Writes "YYYY-MM-DDTHH:MM" into the hidden startsAt/endsAt
// inputs, which the price-breakdown controller reads on change. Server stays the
// source of truth.
export default class extends Controller {
  static targets = [
    "grid", "startInput", "endInput", "label",
    "startTime", "startMeridiem", "endTime", "endMeridiem"
  ]
  static values = { blocked: Array }

  connect() {
    this.blocked = new Set(this.blockedValue)
    this.start = this.startInputTarget.value ? this.startInputTarget.value.slice(0, 10) : null
    this.end = this.endInputTarget.value ? this.endInputTarget.value.slice(0, 10) : null
    this.month = this.start ? new Date(`${this.start}T00:00`) : new Date()
    this.month.setDate(1)
    this.renderCalendar()
  }

  // Reads the "H:MM" (12h) time select + AM/PM toggle into a 24h "HH:MM".
  timeValue(prefix) {
    const [ h12, minute ] = (this[`${prefix}TimeTarget`].value || "9:00").split(":")
    const isPm = this[`${prefix}MeridiemTargets`].some((r) => r.checked && r.value === "PM")
    const hour = parseInt(h12, 10) % 12 + (isPm ? 12 : 0)
    return `${String(hour).padStart(2, "0")}:${minute}`
  }

  renderCalendar() {
    const y = this.month.getFullYear(), mo = this.month.getMonth()
    const first = new Date(y, mo, 1), days = new Date(y, mo + 1, 0).getDate()
    const today = new Date(new Date().toDateString())
    let html = `<div class="flex items-center justify-between mb-2">`
    html += `<button type="button" data-action="availability-calendar#prevMonth" class="text-brand font-semibold px-2">‹</button>`
    html += `<span class="text-sm font-semibold">${first.toLocaleDateString("en-PH", { month: "long", year: "numeric" })}</span>`
    html += `<button type="button" data-action="availability-calendar#nextMonth" class="text-brand font-semibold px-2">›</button></div>`
    html += `<div class="grid grid-cols-7 gap-1 text-center text-xs text-faint mb-1">${["S", "M", "T", "W", "T", "F", "S"].map((d) => `<span>${d}</span>`).join("")}</div>`
    html += `<div class="grid grid-cols-7 gap-1">`
    for (let i = 0; i < first.getDay(); i++) html += `<span></span>`
    for (let d = 1; d <= days; d++) {
      const iso = `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      const blocked = this.blocked.has(iso) || new Date(`${iso}T00:00`) < today
      const base = blocked
        ? "text-faint line-through border border-dashed border-line rounded-lg py-1.5 cursor-not-allowed"
        : "border border-line rounded-lg py-1.5 cursor-pointer hover:border-brand"
      html += `<button type="button" data-iso="${iso}" ${blocked ? "disabled" : 'data-action="availability-calendar#pick"'} class="text-sm font-mono ${base} ${this.rangeClass(iso)}">${d}</button>`
    }
    html += `</div>`
    this.gridTarget.innerHTML = html
  }

  rangeClass(iso) {
    if (iso === this.start || iso === this.end) return "!bg-brand !text-white !border-brand"
    if (this.start && this.end && iso > this.start && iso < this.end) return "!bg-brand-soft !text-brand"
    return ""
  }

  pick(event) {
    const iso = event.currentTarget.dataset.iso
    if (!this.start || (this.start && this.end)) {
      this.start = iso; this.end = null
    } else if (iso < this.start || this.rangeCrossesBlocked(this.start, iso)) {
      this.start = iso; this.end = null
    } else {
      this.end = iso
    }
    this.commit()
    this.renderCalendar()
  }

  rangeCrossesBlocked(a, b) {
    for (let d = new Date(`${a}T00:00`); d <= new Date(`${b}T00:00`); d.setDate(d.getDate() + 1)) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      if (this.blocked.has(iso)) return true
    }
    return false
  }

  commit() {
    this.startInputTarget.value = this.start ? `${this.start}T${this.timeValue("start")}` : ""
    this.endInputTarget.value = this.end ? `${this.end}T${this.timeValue("end")}` : ""
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = this.end
        ? `${this.start} → ${this.end}`
        : (this.start ? `${this.start} — pick a return date` : "Pick your dates")
    }
    this.startInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.endInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  timeChanged() { this.commit() }
  prevMonth() { this.month = new Date(this.month.getFullYear(), this.month.getMonth() - 1, 1); this.renderCalendar() }
  nextMonth() { this.month = new Date(this.month.getFullYear(), this.month.getMonth() + 1, 1); this.renderCalendar() }
}
